import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, Moon, CalendarRange, PartyPopper } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { useApi, errMsg } from "../lib/api";

const Field = ({ id, label, suffix, hint, value, onChange, step = "1" }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <Input id={id} type="number" step={step} min="0" required value={value} onChange={onChange} data-testid={`settings-${id}-input`} className="h-12 bg-background pr-16 font-num text-lg" />
      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span>
    </div>
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const Card = ({ title, children }) => (
  <section className="space-y-5 rounded-2xl border border-border/70 bg-card p-5 sm:p-6">
    <h2 className="font-heading text-xl font-extrabold uppercase tracking-tight">{title}</h2>
    {children}
  </section>
);

const LOCKED = [
  { icon: Moon, label: "Night shift bonus" },
  { icon: CalendarRange, label: "Weekend bonus" },
  { icon: PartyPopper, label: "Public holiday bonus" },
];

export default function SettingsPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api("get", "/settings") });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: () => api("put", "/settings", {
      ...form,
      hourly_rate: Number(form.hourly_rate), daily_ot_hours: Number(form.daily_ot_hours), weekly_ot_hours: Number(form.weekly_ot_hours),
      ot_rate_pct: Number(form.ot_rate_pct), auto_break_after_hours: Number(form.auto_break_after_hours), auto_break_minutes: Number(form.auto_break_minutes),
    }),
    onSuccess: (s) => { qc.setQueryData(["settings"], s); qc.invalidateQueries({ queryKey: ["overview"] }); toast.success("Salary settings saved"); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!form) return <div className="h-64 animate-pulse rounded-2xl bg-card" />;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="settings-page" className="mx-auto max-w-2xl space-y-8">
      <div>
        <p className="font-num text-xs font-bold uppercase tracking-wider text-amber-500">Your pay rules</p>
        <h1 className="mt-1 font-heading text-4xl font-black uppercase tracking-tight sm:text-5xl">Salary Settings</h1>
      </div>
      <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Card title="Pay">
          <Field id="hourly-rate" label="Hourly rate" suffix="€ / h" step="0.01" value={form.hourly_rate} onChange={set("hourly_rate")} />
        </Card>
        <Card title="Overtime">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="daily-ot" label="Daily limit" suffix="h / day" step="0.25" value={form.daily_ot_hours} onChange={set("daily_ot_hours")} />
            <Field id="weekly-ot" label="Weekly limit" suffix="h / week" step="0.5" value={form.weekly_ot_hours} onChange={set("weekly_ot_hours")} />
          </div>
          <Field id="ot-rate" label="Overtime rate" suffix="%" value={form.ot_rate_pct} onChange={set("ot_rate_pct")} hint="125% means each overtime hour pays 1.25× your hourly rate." />
        </Card>
        <Card title="Automatic break">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="auto-break">Deduct a break automatically</Label>
            <Switch id="auto-break" checked={form.auto_break_enabled} data-testid="settings-auto-break-switch"
              onCheckedChange={(v) => setForm((f) => ({ ...f, auto_break_enabled: v }))} />
          </div>
          {form.auto_break_enabled && (
            <div className="grid gap-5 sm:grid-cols-2">
              <Field id="break-minutes" label="Break length" suffix="min" value={form.auto_break_minutes} onChange={set("auto_break_minutes")} />
              <Field id="break-after" label="For shifts longer than" suffix="hours" step="0.5" value={form.auto_break_after_hours} onChange={set("auto_break_after_hours")} />
            </div>
          )}
        </Card>
        <button type="submit" disabled={save.isPending} data-testid="settings-save-btn"
          className="w-full rounded-full bg-amber-500 py-3.5 font-bold text-[#0B0F17] hover:bg-amber-400 active:scale-[0.98] disabled:opacity-60">
          {save.isPending ? "Saving…" : "Save settings"}
        </button>
      </form>
      <Link to="/premium" data-testid="settings-advanced-locked" className="block rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 transition-colors hover:border-amber-500/60">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-extrabold uppercase">Advanced calculations</h2>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-[10px] font-bold uppercase text-[#0B0F17]"><Lock className="h-3 w-3" />Premium</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {LOCKED.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</span>
          ))}
        </div>
      </Link>
    </div>
  );
}
